import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Divider,
} from '@mui/material';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LinkIcon from '@mui/icons-material/Link';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import EmojiPicker from 'emoji-picker-react';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  initialData?: any;
}

export const WhatsappTemplateModal = ({ open, onClose, onSave, initialData }: Props) => {
  const [title, setTitle] = useState(initialData?.title || '');
  const [content, setContent] = useState(initialData?.content || '');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Variables dinámicas
  const variables = [
    { label: 'Nombre Cliente', value: '[Nombre del Cliente]', icon: <PersonIcon fontSize="small" /> },
    { label: 'Nombre Evento', value: '[Nombre del Evento]', icon: <EventIcon fontSize="small" /> },
    { label: 'Fecha Evento', value: '[Fecha del Evento]', icon: <CalendarMonthIcon fontSize="small" /> },
    { label: 'Link Encuesta', value: '[Link]', icon: <LinkIcon fontSize="small" /> },
  ];

  const handleInsertText = (text: string, isFormatting: boolean = false) => {
    const textarea = document.getElementById('template-content-textarea') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentText = content;
      
      let newText = '';
      if (isFormatting) {
        // Envolver el texto seleccionado o insertar formato vacío
        const selectedText = currentText.substring(start, end);
        newText = currentText.substring(0, start) + text + selectedText + text + currentText.substring(end);
      } else {
        // Insertar texto (variable o emoji)
        newText = currentText.substring(0, start) + text + currentText.substring(end);
      }
      
      setContent(newText);
      
      // Enfocar de nuevo (usando setTimeout para esperar el render)
      setTimeout(() => {
        textarea.focus();
        const newCursorPos = isFormatting ? start + text.length + (end - start) : start + text.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    } else {
      setContent(content + text);
    }
  };

  const onEmojiClick = (emojiObject: any) => {
    handleInsertText(emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  const handleSubmit = () => {
    onSave({
      title,
      content,
    });
  };

  // Actualizar estado si cambia el initialData al abrir
  React.useEffect(() => {
    if (open) {
      setTitle(initialData?.title || '');
      setContent(initialData?.content || '');
      setShowEmojiPicker(false);
    }
  }, [open, initialData]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialData ? 'Editar Plantilla' : 'Crear Plantilla'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            label="Título de la Plantilla"
            variant="outlined"
            size="small"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ej: Agradecimiento Eventos"
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>Contenido del Mensaje</Typography>
            
            <Paper variant="outlined" sx={{ mb: 1, p: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', bgcolor: '#f5f5f5' }}>
              
              <Tooltip title="Emojis">
                <IconButton size="small" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                  <EmojiEmotionsIcon fontSize="small" sx={{ color: '#ff9800' }} />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              
              <Tooltip title="Negrita (*)">
                <IconButton size="small" onClick={() => handleInsertText('*', true)}>
                  <FormatBoldIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cursiva (_)">
                <IconButton size="small" onClick={() => handleInsertText('_', true)}>
                  <FormatItalicIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Tachado (~)">
                <IconButton size="small" onClick={() => handleInsertText('~', true)}>
                  <StrikethroughSIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              {variables.map((v) => (
                <Tooltip key={v.value} title={`Insertar ${v.label}`}>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    sx={{ textTransform: 'none', py: 0, px: 1, minWidth: 'auto', color: 'text.secondary', borderColor: '#ccc' }}
                    startIcon={v.icon}
                    onClick={() => handleInsertText(v.value)}
                  >
                    {v.label}
                  </Button>
                </Tooltip>
              ))}
            </Paper>

            {showEmojiPicker && (
              <Box sx={{ position: 'absolute', zIndex: 10, mt: 1 }}>
                <EmojiPicker onEmojiClick={onEmojiClick} />
              </Box>
            )}

            <TextField
              id="template-content-textarea"
              multiline
              rows={8}
              fullWidth
              variant="outlined"
              placeholder="Escribe el mensaje de WhatsApp aquí..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onClick={() => setShowEmojiPicker(false)}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Puedes usar formato de WhatsApp: *negrita*, _cursiva_, ~tachado~.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={!title.trim() || !content.trim()}
          sx={{ bgcolor: '#4A148C', '&:hover': { bgcolor: '#380b6b' } }}
        >
          {initialData ? 'Guardar Cambios' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
